import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  driveCreateFolder,
  driveDelete,
  driveDisconnect,
  driveMove,
  driveRename,
  driveRoot,
  driveStatus,
  driveUpload,
  driveView,
  isDriveFolder,
  startGoogleDriveAuth,
} from './driveApi';
import MoveModal from './MoveModal';
import ConfirmModal from '../../components/ConfirmModal';
import { API_BASE_URL } from '../../lib/apiBaseUrl';
import './AssetsExplorer.css';

const ROOT_FOLDER_NAME = 'Home';
const ASSETS_ROOT_TYPE = 'assets';

const formatBytes = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size < 10 && unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const decodeUrlValue = (value = '') => {
  try {
    return decodeURIComponent(value);
  } catch (_err) {
    return String(value || '');
  }
};

const mapOAuthErrorMessage = (errorCodeRaw = '') => {
  const errorCode = decodeUrlValue(errorCodeRaw).trim();
  const normalized = errorCode.toUpperCase();

  if (!errorCode) return 'Google Drive connection failed. Please try again.';
  if (normalized === 'MISSING_CODE_OR_STATE') {
    return 'Google Drive authorization was cancelled or expired. Please try again.';
  }
  if (normalized === 'MISSING_REFRESH_TOKEN') {
    return 'Google Drive did not return a refresh token. Please reconnect and allow access.';
  }
  if (normalized === 'OAUTH_INVALID_STATE') {
    return 'Google Drive verification expired. Please reconnect.';
  }
  if (normalized === 'DRIVE_RECONNECT_REQUIRED') {
    return 'Drive access expired. Please reconnect Google Drive.';
  }
  if (normalized === 'INTERNAL_ERROR') {
    return 'Google Drive connection failed on the server. Please try again in a moment.';
  }
  return `Google Drive connection failed (${errorCode}). Please try again.`;
};

const AssetsExplorer = ({ firebase }) => {
  const [rootFolderId, setRootFolderId] = useState(null);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [view, setView] = useState({ folderId: null, breadcrumb: [], items: [] });
  const [loadingRoot, setLoadingRoot] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState('');
  const [callbackNotice, setCallbackNotice] = useState('');
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameItem, setRenameItem] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteItem, setDeleteItem] = useState(null);
  const fileInputRef = useRef(null);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [moveItem, setMoveItem] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [pendingFolderId, setPendingFolderId] = useState(null);
  const requestIdRef = useRef(0);
  const initializedRef = useRef(false);
  const visibleMessage = error || callbackNotice;

  const connected = Boolean(rootFolderId);
  const currentUserEmail = firebase?.auth?.currentUser?.email || '';

  const resolveErrorMessage = useCallback(
    (err, fallback) => {
      const message = typeof err?.message === 'string' && err.message ? err.message : fallback;
      if (err?.code === 'DRIVE_BACKEND_UNREACHABLE' || /failed to fetch/i.test(message)) {
        return `Cannot reach Drive backend at ${API_BASE_URL}. Make sure the server is running and REACT_APP_API_BASE_URL is correct.`;
      }
      if (err?.reconnect) {
        return 'Drive access expired. Please reconnect Google Drive.';
      }
      return message;
    },
    [],
  );

  const loadView = useCallback(
    async (folderIdOverride) => {
      const folderIdToLoad = folderIdOverride || rootFolderId;
      if (!firebase || !folderIdToLoad) return;
      if (pendingFolderId === folderIdToLoad) return;

      const requestId = ++requestIdRef.current;
      setPendingFolderId(folderIdToLoad);
      setLoadingList(true);
      setError('');

      try {
        const data = await driveView(firebase, folderIdToLoad, {}, { rootType: ASSETS_ROOT_TYPE });
        if (requestId !== requestIdRef.current) return;
        setView({
          folderId: data.folderId,
          breadcrumb: data.breadcrumb || [],
          items: data.items || [],
        });
        setCurrentFolderId(data.folderId);
        setNeedsReconnect(false);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error('Failed to load Drive view:', err);
        setError(resolveErrorMessage(err, 'Failed to load Drive view.'));
        if (err?.reconnect) {
          setNeedsReconnect(true);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoadingList(false);
          setPendingFolderId(null);
        }
      }
    },
    [firebase, pendingFolderId, resolveErrorMessage, rootFolderId],
  );

  const initialize = useCallback(async () => {
    if (!firebase || initializedRef.current) return;

    setLoadingRoot(true);
    setError('');
    try {
      const status = await driveStatus(firebase, { rootType: ASSETS_ROOT_TYPE });
      if (!status?.connected) {
        setRootFolderId(null);
        setView({ folderId: null, breadcrumb: [], items: [] });
        initializedRef.current = true;
        return;
      }

      const rootId = status.rootFolderId || (await driveRoot(firebase, { rootType: ASSETS_ROOT_TYPE }));
      setRootFolderId(rootId);

      if (rootId) {
        await loadView(rootId);
      }
      initializedRef.current = true;
    } catch (err) {
      console.error('Failed to initialize Drive explorer:', err);
      setError(resolveErrorMessage(err, 'Failed to load Drive connection.'));
    } finally {
      setLoadingRoot(false);
    }
  }, [firebase, loadView, resolveErrorMessage]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const currentUrl = new URL(window.location.href);
    const oauthError = currentUrl.searchParams.get('error');
    const hadConnectedFlag = currentUrl.searchParams.get('connected') === '1';
    if (!oauthError && !hadConnectedFlag) return;

    if (oauthError) {
      setCallbackNotice(mapOAuthErrorMessage(oauthError));
    } else if (hadConnectedFlag) {
      setCallbackNotice('');
    }

    currentUrl.searchParams.delete('error');
    currentUrl.searchParams.delete('connected');
    const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    window.history.replaceState({}, document.title, nextUrl);
  }, []);

  const handleConnect = async () => {
    if (!firebase) return;

    setConnecting(true);
    setError('');
    setCallbackNotice('');
    try {
      const email = currentUserEmail;
      const domain = email.includes('@') ? email.split('@')[1] : '';
      const url = await startGoogleDriveAuth(firebase, {
        loginHint: email || undefined,
        hd: domain || undefined,
      });
      window.location.assign(url);
    } catch (err) {
      console.error('Failed to start Google OAuth:', err);
      setError(resolveErrorMessage(err, 'Failed to start Google Drive connection.'));
    } finally {
      setConnecting(false);
    }
  };

  const handleNavigateToFolder = async (folder) => {
    if (!folder?.id || !isDriveFolder(folder) || loadingList) return;
    await loadView(folder.id);
  };

  const confirmDisconnect = async () => {
    if (disconnecting) return;
    setDisconnecting(true);
    setError('');
    setCallbackNotice('');
    try {
      await driveDisconnect(firebase, { rootType: ASSETS_ROOT_TYPE });
      setRootFolderId(null);
      setCurrentFolderId(null);
      setView({ folderId: null, breadcrumb: [], items: [] });
      setNeedsReconnect(false);
      setShowDisconnectModal(false);
    } catch (err) {
      console.error('Failed to disconnect Google Drive for assets:', err);
      setError(resolveErrorMessage(err, 'Failed to disconnect Google Drive.'));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleNavigateToBreadcrumb = async (index) => {
    const crumb = view.breadcrumb[index];
    if (!crumb?.id || loadingList) return;
    await loadView(crumb.id);
  };

  const handleCreateFolder = async () => {
    const trimmedName = newFolderName.trim();
    if (!trimmedName) {
      setError('Folder name cannot be empty.');
      return;
    }

    setError('');
    setCallbackNotice('');
    try {
      const targetFolder = currentFolderId || rootFolderId;
      await driveCreateFolder(firebase, targetFolder, trimmedName, { rootType: ASSETS_ROOT_TYPE });
      setShowNewFolderModal(false);
      setNewFolderName('');
      await loadView(targetFolder);
    } catch (err) {
      console.error('Failed to create folder:', err);
      setError(resolveErrorMessage(err, 'Failed to create folder.'));
    }
  };

  const handleUploadChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setError('');
    setCallbackNotice('');
    try {
      const targetFolder = currentFolderId || rootFolderId;
      await driveUpload(firebase, targetFolder, file, { rootType: ASSETS_ROOT_TYPE });
      await loadView(targetFolder);
    } catch (err) {
      console.error('Failed to upload file:', err);
      setError(resolveErrorMessage(err, 'Failed to upload file.'));
    }
  };

  const handleRename = (file) => {
    if (!file) return;
    setRenameItem(file);
    setRenameValue(file.name || '');
  };

  const handleMove = (file) => {
    setMoveItem(file);
  };

  const handleDelete = (file) => {
    if (!file) return;
    setDeleteItem(file);
  };

  const confirmRename = async () => {
    if (!renameItem) return;
    const nextName = renameValue.trim();
    if (!nextName || nextName === renameItem.name) {
      setRenameItem(null);
      return;
    }
    setError('');
    setCallbackNotice('');
    try {
      await driveRename(firebase, renameItem.id, nextName, { rootType: ASSETS_ROOT_TYPE });
      setRenameItem(null);
      await loadView(currentFolderId || rootFolderId);
    } catch (err) {
      console.error('Failed to rename item:', err);
      setError(resolveErrorMessage(err, 'Failed to rename item.'));
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setError('');
    setCallbackNotice('');
    try {
      await driveDelete(firebase, deleteItem.id, { rootType: ASSETS_ROOT_TYPE });
      setDeleteItem(null);
      await loadView(currentFolderId || rootFolderId);
    } catch (err) {
      console.error('Failed to delete item:', err);
      setError(resolveErrorMessage(err, 'Failed to delete item.'));
    }
  };

  const sortedFiles = useMemo(() => {
    const list = [...view.items];
    list.sort((a, b) => {
      const aIsFolder = isDriveFolder(a);
      const bIsFolder = isDriveFolder(b);
      if (aIsFolder && !bIsFolder) return -1;
      if (!aIsFolder && bIsFolder) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return list;
  }, [view.items]);

  const handleDragStart = (event, file) => {
    event.dataTransfer.setData(
      'application/json',
      JSON.stringify({ id: file.id, name: file.name, mimeType: file.mimeType }),
    );
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDropOnFolder = async (event, folder) => {
    event.preventDefault();
    setDragOverFolderId(null);
    let payload;
    try {
      payload = JSON.parse(event.dataTransfer.getData('application/json'));
    } catch (_err) {
      return;
    }
    if (!payload?.id || payload.id === folder.id) {
      return;
    }
    const confirmed = window.confirm(`Move 1 item to "${folder.name || 'Untitled folder'}"?`);
    if (!confirmed) return;

    setError('');
    setCallbackNotice('');
    try {
      await driveMove(firebase, payload.id, folder.id, { rootType: ASSETS_ROOT_TYPE });
      await loadView(currentFolderId || rootFolderId);
    } catch (err) {
      console.error('Failed to move item via drag/drop:', err);
      setError(resolveErrorMessage(err, "Can't move there."));
    }
  };

  if (loadingRoot) {
    return (
      <div className="welcome-section assets-explorer">
        <h2>Assets</h2>
        <p className="welcome-text">Checking your Google Drive connection...</p>
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="welcome-section assets-explorer">
        <h2>Assets</h2>
        <p className="welcome-text">Connect Google Drive to manage your assets.</p>
        {currentUserEmail ? (
          <p className="welcome-text" style={{ marginTop: 0 }}>
            Will connect as: <strong>{currentUserEmail}</strong>
          </p>
        ) : null}
        <button className="btn-primary" type="button" onClick={handleConnect} disabled={connecting}>
          {connecting ? 'Connecting...' : 'Connect Google Drive'}
        </button>
        {visibleMessage ? <p style={{ marginTop: '0.75rem' }}>{visibleMessage}</p> : null}
      </div>
    );
  }

  return (
    <div className="welcome-section assets-explorer" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <header style={{ textAlign: 'left' }}>
        <h2 className="assets-title" style={{ marginBottom: '0.35rem' }}>
          Google Drive Connected Successfully
        </h2>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <nav aria-label="Breadcrumb" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {view.breadcrumb.map((crumb, index) => {
            const isLast = index === view.breadcrumb.length - 1;
            const label = index === 0 ? ROOT_FOLDER_NAME : (crumb.name || 'Folder');
            return (
              <button
                key={crumb.id}
                type="button"
                onClick={() => handleNavigateToBreadcrumb(index)}
                disabled={isLast || loadingList}
                className="nav-button"
                style={{ padding: '0.3rem 0.55rem' }}
              >
                {label}
              </button>
            );
          })}
        </nav>

        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button type="button" className="btn-secondary" onClick={() => setShowNewFolderModal(true)} disabled={loadingList}>
            New Folder
          </button>
          <button type="button" className="btn-secondary" onClick={() => fileInputRef.current?.click?.()} disabled={loadingList}>
            Upload File
          </button>
          <button type="button" className="btn-secondary" onClick={() => loadView(currentFolderId || rootFolderId)} disabled={loadingList}>
            {loadingList ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowDisconnectModal(true)}
            disabled={loadingList || disconnecting || connecting}
          >
            {disconnecting ? 'Disconnecting...' : 'Disconnect Google Drive'}
          </button>
          {needsReconnect ? (
            <button type="button" className="btn-secondary" onClick={handleConnect}>
              Reconnect Google Drive
            </button>
          ) : null}
          <input ref={fileInputRef} type="file" onChange={handleUploadChange} style={{ display: 'none' }} />
        </div>
      </div>

      {visibleMessage ? <p>{visibleMessage}</p> : null}

      <div style={{ overflowX: 'auto', position: 'relative' }}>
        {loadingList ? (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 12,
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
              padding: '0.25rem 0.6rem',
              borderRadius: 999,
              fontSize: '0.75rem',
              zIndex: 2,
            }}
          >
            Loading...
          </div>
        ) : null}
        <table className="data-table" style={{ width: '100%', minWidth: 680 }}>
          <thead>
            <tr>
              <th align="left">Name</th>
              <th align="left">Type</th>
              <th align="left">Size</th>
              <th align="left">Modified</th>
              <th align="left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedFiles.map((file) => {
              const folder = isDriveFolder(file);
              const isDragOver = dragOverFolderId === file.id;
              return (
                <tr
                  key={file.id}
                  draggable
                  onDragStart={(event) => handleDragStart(event, file)}
                  onDragOver={
                    folder
                      ? (event) => {
                          event.preventDefault();
                          setDragOverFolderId(file.id);
                        }
                      : undefined
                  }
                  onDragLeave={folder ? () => setDragOverFolderId(null) : undefined}
                  onDrop={folder ? (event) => handleDropOnFolder(event, file) : undefined}
                  style={{
                    background: isDragOver ? 'rgba(255, 255, 255, 0.08)' : undefined,
                  }}
                >
                  <td>
                    {folder ? (
                      <button
                        type="button"
                        onClick={() => handleNavigateToFolder(file)}
                        className="nav-button"
                        disabled={loadingList}
                        onDragOver={(event) => {
                          event.preventDefault();
                          setDragOverFolderId(file.id);
                        }}
                        onDragLeave={() => setDragOverFolderId(null)}
                        onDrop={(event) => handleDropOnFolder(event, file)}
                        style={{
                          border: isDragOver ? '1px solid rgba(255,255,255,0.5)' : undefined,
                        }}
                      >
                        {file.name || 'Untitled folder'}
                      </button>
                    ) : file.webViewLink ? (
                      <a href={file.webViewLink} target="_blank" rel="noreferrer">
                        {file.name || 'Untitled file'}
                      </a>
                    ) : (
                      file.name || 'Untitled file'
                    )}
                  </td>
                  <td>{folder ? 'Folder' : file.mimeType || 'File'}</td>
                  <td>{folder ? '-' : formatBytes(file.size)}</td>
                  <td>{formatDate(file.modifiedTime)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => handleRename(file)}
                        style={{ padding: '0.45rem 0.9rem' }}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => handleMove(file)}
                        style={{ padding: '0.45rem 0.9rem' }}
                      >
                        Move
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => handleDelete(file)}
                        style={{ padding: '0.45rem 0.9rem' }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!sortedFiles.length ? (
              <tr>
                <td colSpan={5} style={{ padding: '1rem', textAlign: 'center', opacity: 0.8 }}>
                  {loadingList ? 'Loading assets...' : 'No files yet. Upload something.'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {showNewFolderModal ? (
        <div role="dialog" aria-modal="true" className="studio-modal-overlay">
          <div className="studio-modal">
            <div className="studio-modal-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeWidth="1.5" d="M12 5v14M5 12h14" />
              </svg>
            </div>
            <h3 className="studio-modal-title">Create Folder</h3>
            <p className="studio-modal-subtitle">Enter a name for your folder.</p>
            <input
              type="text"
              value={newFolderName}
              onChange={(event) => setNewFolderName(event.target.value)}
              placeholder="e.g. Campaign Assets"
              className="studio-modal-input"
              autoFocus
            />
            <div className="studio-modal-actions">
              <button type="button" className="studio-modal-button primary" onClick={handleCreateFolder}>
                Create
              </button>
              <button type="button" className="studio-modal-button secondary" onClick={() => setShowNewFolderModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {renameItem ? (
        <div role="dialog" aria-modal="true" className="studio-modal-overlay">
          <div className="studio-modal">
            <div className="studio-modal-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeWidth="1.5" d="M4 20h4l10-10-4-4L4 16v4z" />
              </svg>
            </div>
            <h3 className="studio-modal-title">Rename Item</h3>
            <p className="studio-modal-subtitle">Enter a new name.</p>
            <input
              type="text"
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              className="studio-modal-input"
              autoFocus
            />
            <div className="studio-modal-actions">
              <button type="button" className="studio-modal-button primary" onClick={confirmRename}>
                Save
              </button>
              <button type="button" className="studio-modal-button secondary" onClick={() => setRenameItem(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteItem ? (
        <div role="dialog" aria-modal="true" className="studio-modal-overlay">
          <div className="studio-modal">
            <div className="studio-modal-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeWidth="1.5" d="M3 6h18M8 6V4h8v2m-1 0v14H9V6h6z" />
              </svg>
            </div>
            <h3 className="studio-modal-title">Delete Item</h3>
            <p className="studio-modal-subtitle">This cannot be undone.</p>
            <div className="studio-modal-actions">
              <button type="button" className="studio-modal-button primary" onClick={confirmDelete}>
                Delete
              </button>
              <button type="button" className="studio-modal-button secondary" onClick={() => setDeleteItem(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        isOpen={showDisconnectModal}
        onClose={() => !disconnecting && setShowDisconnectModal(false)}
        onConfirm={confirmDisconnect}
        onCancel={() => !disconnecting && setShowDisconnectModal(false)}
        title="Disconnect Google Drive"
        message="This disconnects only Branding & Marketing assets. Studio and other sections stay connected."
        confirmText={disconnecting ? 'Disconnecting...' : 'Disconnect'}
        cancelText="Cancel"
      />

      {moveItem ? (
        <MoveModal
          open={Boolean(moveItem)}
          onClose={() => setMoveItem(null)}
          item={moveItem}
          firebase={firebase}
          rootFolderId={rootFolderId}
          currentFolderId={currentFolderId}
          rootLabel={ROOT_FOLDER_NAME}
          onMoved={async () => {
            await loadView(currentFolderId || rootFolderId);
          }}
        />
      ) : null}
    </div>
  );
};

export default AssetsExplorer;
